import { useEffect, useRef, useState } from 'react';
import { Bot, Download, FilePenLine, Mic, Phone, Play, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { uploadFile } from '../lib/upload';
import { FileDrop } from './FileDrop';
import { TimedText } from './TimedText';

/**
 * Call recording: sube grabaciones de llamadas complicadas (approval calls, disputas) con
 * adjuster / homeowner / contractor. Deja escribir el TIPO y elegir CON QUIÉN fue. Aparece en
 * TODOS los departamentos (mismo dato a nivel claim) y también en Files (pestaña Recordings).
 * Guarda quién la subió + fecha; el activity log deja el track.
 */
type Rec = {
  id: string; recording_type: string | null; party: string | null; party_name: string | null;
  file_name: string | null; mime_type: string | null; department_key: string | null;
  uploaded_by: string | null; created_at: string;
  source: string | null; counterparty_number: string | null; call_direction: string | null;
  ai_summary: string | null; ai_transcript: string | null; ai_transcript_labeled: string | null;
  ai_status: string | null; ai_error: string | null;
};
const PARTIES = ['adjuster', 'homeowner', 'contractor', 'other'] as const;
const isAudio = (n: string | null, m: string | null) => (m?.startsWith('audio/') ?? false) || /\.(mp3|m4a|wav|aac|ogg|opus)$/i.test(n ?? '');

export function CallRecordings({ claimId, departmentKey, title }: { claimId: string; departmentKey?: string; title?: string }) {
  const { t } = useI18n();
  const [rows, setRows] = useState<Rec[]>([]);
  const [type, setType] = useState('');
  const [party, setParty] = useState<string>('');
  const [partyName, setPartyName] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [sumBusy, setSumBusy] = useState<Record<string, boolean>>({});
  const [openTx, setOpenTx] = useState<Record<string, boolean>>({});
  // Resumen IA cerrado por default (2026-08-10): con varias llamadas se apilaban y había que
  // scrollear de más para llegar al calendario de agendar.
  const [openSum, setOpenSum] = useState<Record<string, boolean>>({});
  // Un <audio> por grabación: las marcas de tiempo del resumen mueven el reproductor de ESA fila.
  const players = useRef<Record<string, HTMLAudioElement | null>>({});
  const seekTo = (id: string) => (seconds: number) => {
    const el = players.current[id];
    if (!el) return;
    el.currentTime = seconds;
    void el.play().catch(() => { /* algunos navegadores exigen un gesto previo */ });
  };

  async function load() {
    try { setRows((await api.get<{ recordings: Rec[] }>(`/api/claims/${claimId}/recordings`)).recordings); } catch { /* ignore */ }
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [claimId]);

  async function onUpload(file: File) {
    setBusy(true); setMsg(null);
    try {
      const key = await uploadFile(file, 'misc');
      await api.post(`/api/claims/${claimId}/recordings`, {
        fileKey: key, fileName: file.name, mimeType: file.type || null,
        recordingType: type.trim() || null, party: party || null, partyName: partyName.trim() || null,
        departmentKey: departmentKey ?? null,
      });
      setType(''); setParty(''); setPartyName(''); setMsg(t('rec.saved'));
      await load();
    } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }

  async function del(id: string) {
    if (!window.confirm(t('rec.deleteConfirm'))) return;
    try { await api.del(`/api/recordings/${id}`); await load(); } catch { /* ignore */ }
  }

  // Resumen IA bajo demanda: para grabaciones viejas (sin resumen) o para reintentar las que
  // fallaron. Corre en background → refrescamos unas veces hasta que aparezca.
  async function summarize(id: string) {
    setSumBusy((m) => ({ ...m, [id]: true }));
    try {
      await api.post(`/api/recordings/${id}/summarize`, {});
      for (let i = 0; i < 10; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const fresh = (await api.get<{ recordings: Rec[] }>(`/api/claims/${claimId}/recordings`)).recordings;
        setRows(fresh);
        const me = fresh.find((x) => x.id === id);
        if (me?.ai_summary || me?.ai_status === 'failed') break;
      }
    } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Error'); }
    finally { setSumBusy((m) => ({ ...m, [id]: false })); }
  }

  // URL firmada (cacheada) para reproducir/descargar.
  async function urlFor(id: string): Promise<string | null> {
    if (urls[id]) return urls[id];
    const u = `/api/recordings/${id}/url`;
    setUrls((m) => ({ ...m, [id]: u }));
    return u;
  }

  /**
   * PLEGADO POR DEFECTO — pedido del equipo (2026-08-21): "collapse all call recordings".
   * El módulo va en TODOS los departamentos y desplegado ocupa media pantalla con un formulario
   * de subida que casi nunca se usa al abrir el claim. La cabecera dice cuántas hay, así que
   * plegado no esconde nada: quien las necesita las abre.
   */
  return (
    <details className="card">
      <summary className="muted" style={{ cursor: 'pointer', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
        <Mic size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {title ?? t('rec.title')}
        {rows.length > 0 && <span style={{ marginLeft: 6, opacity: 0.8 }}>({rows.length})</span>}
      </summary>

      {/* Formulario: tipo (texto libre) + con quién + nombre opcional + subir. */}
      <div style={{ display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input className="input sm" style={{ flex: 2, minWidth: 160 }} placeholder={t('rec.typePh')} value={type} onChange={(e) => setType(e.target.value)} />
          <select className="input sm" style={{ flex: 1, minWidth: 130 }} value={party} onChange={(e) => setParty(e.target.value)}>
            <option value="">{t('rec.withPh')}</option>
            {PARTIES.map((p) => <option key={p} value={p}>{t(`rec.party.${p}`)}</option>)}
          </select>
          <input className="input sm" style={{ flex: 1, minWidth: 130 }} placeholder={t('rec.partyNamePh')} value={partyName} onChange={(e) => setPartyName(e.target.value)} />
        </div>
        <FileDrop onFile={onUpload} accept="audio/*,video/*,application/pdf" label={t('rec.upload')} busy={busy} busyLabel={t('rec.uploading')} compact />
        {msg && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{msg}</div>}
      </div>

      {/* Lista de grabaciones: tipo · con quién · quién subió · fecha + reproducir/borrar. */}
      {rows.length > 0 && (
        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          {rows.map((r) => (
            <div key={r.id} style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '8px 10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-sm)' }}><Mic size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {r.recording_type || r.file_name || `#${r.id}`}</span>
                {r.source === 'ringcentral' && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--green-soft)', color: 'var(--green)' }} title={t('rec.autoHint')}><span className="dot red" /> {t('rec.auto')}{r.call_direction ? ` · ${r.call_direction === 'Inbound' ? '↙' : '↗'}` : ''}</span>}
                {r.party && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--accent-soft)', color: 'var(--accent)' }}>{t(`rec.party.${r.party}`)}{r.party_name ? ` · ${r.party_name}` : ''}</span>}
                {r.counterparty_number && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}><Phone size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {r.counterparty_number}</span>}
                <span style={{ flex: 1 }} />
                <button className="btn ghost sm" onClick={async () => { const u = await urlFor(r.id); if (u) window.open(u, '_blank', 'noopener'); }}>{isAudio(r.file_name, r.mime_type) ? <><Play size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('rec.play')}</> : <><Download size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('rec.open')}</>}</button>
                <button className="btn ghost sm" onClick={() => del(r.id)}><X size={13} strokeWidth={1.75} /></button>
              </div>
              {/* Resumen IA DESPLEGABLE, cerrado por default (pedido 2026-08-10: "se acumulan
                  muchos y hay que scrollear innecesariamente para llegar al calendario"). El
                  renglón cerrado muestra el arranque del resumen como teaser; un click lo abre
                  completo, con la transcripción desplegable debajo. */}
              {r.ai_summary && (
                <div style={{ marginTop: 6, background: 'var(--surface-2)', borderLeft: '3px solid var(--accent)', borderRadius: 8, padding: '8px 10px' }}>
                  <button type="button" onClick={() => setOpenSum((p) => ({ ...p, [r.id]: !p[r.id] }))}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', border: 0, background: 'transparent', cursor: 'pointer', padding: 0, textAlign: 'left' }}>
                    <span style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', color: 'var(--accent)', whiteSpace: 'nowrap' }}>
                      <Bot size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('rec.aiSummary')} {openSum[r.id] ? '▾' : '▸'}
                    </span>
                    {!openSum[r.id] && (
                      <span className="muted" style={{ fontSize: 'var(--fs-2xs)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {r.ai_summary.replace(/\s+/g, ' ').slice(0, 160)}
                      </span>
                    )}
                  </button>
                  {openSum[r.id] && (
                  <>
                  <div style={{ fontSize: 'var(--fs-sm)', whiteSpace: 'pre-wrap', lineHeight: 1.45, marginTop: 5 }}>
                    <TimedText text={r.ai_summary} onSeek={isAudio(r.file_name, r.mime_type) ? seekTo(r.id) : undefined} />
                  </div>
                  {r.ai_transcript && (
                    <div style={{ marginTop: 6 }}>
                      <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }}
                        onClick={() => setOpenTx((p) => ({ ...p, [r.id]: !p[r.id] }))}>
                        <FilePenLine size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {openTx[r.id] ? t('cm.hideTranscript') : t('cm.showTranscript')} {openTx[r.id] ? '▾' : '▸'}
                      </button>
                      {openTx[r.id] && (
                        /* La etiquetada (quién dijo qué y cuándo) si la hay; si no, la cruda de Whisper. */
                        <div style={{ fontSize: 'var(--fs-xs)', whiteSpace: 'pre-wrap', lineHeight: 1.7, marginTop: 5, maxHeight: 380, overflowY: 'auto' }}>
                          <TimedText text={r.ai_transcript_labeled || r.ai_transcript} onSeek={isAudio(r.file_name, r.mime_type) ? seekTo(r.id) : undefined} />
                        </div>
                      )}
                    </div>
                  )}
                  </>
                  )}
                </div>
              )}
              {!r.ai_summary && isAudio(r.file_name, r.mime_type) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                  <button className="btn ghost sm" disabled={!!sumBusy[r.id]} onClick={() => summarize(r.id)}>
                    <Bot size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {sumBusy[r.id] ? t('rec.aiWorking') : t('rec.aiGenerate')}
                  </button>
                  {r.ai_status === 'failed' && r.ai_error && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{r.ai_error}</span>}
                </div>
              )}
              {/* Reproductor embebido para audio. */}
              {isAudio(r.file_name, r.mime_type) && <audio ref={(el) => { players.current[r.id] = el; }} controls preload="none" src={`/api/recordings/${r.id}/url`} style={{ width: '100%', marginTop: 6, height: 34 }} />}
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 4 }}>
                {r.file_name ? `${r.file_name} · ` : ''}{t('rec.by')} {r.uploaded_by || '—'} · {new Date(r.created_at).toLocaleString('en-US')}
              </div>
            </div>
          ))}
        </div>
      )}
    </details>
  );
}
